/**
 * Slice 50 / O1–O3 — Unit tab → Arrival checklist sheet.
 * The owner's "When you arrive" tasks: wording, order, Required, Needs a photo.
 * A property that has never saved a list shows (and its cleaners use) the starter list;
 * after the first Save the saved list is the list, even when it is empty.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import {
  arrivalSummary,
  effectiveArrivalTasks,
  newArrivalTaskId,
  serializeArrivalTasks,
  type ArrivalTask,
} from "@/lib/arrivalChecklist";

function useArrivalChecklistRaw(propertyId: string) {
  return useQuery({
    queryKey: ["arrival-checklist", propertyId],
    queryFn: async (): Promise<{ raw: unknown }> => {
      const { data, error } = await supabase
        .from("properties")
        .select("arrival_checklist")
        .eq("id", propertyId)
        .maybeSingle();
      if (error) throw error;
      return { raw: (data as unknown as { arrival_checklist: unknown } | null)?.arrival_checklist ?? null };
    },
  });
}

/** One-line summary for the Unit tab row. */
export function useArrivalChecklistSummary(propertyId: string): string | null {
  const { data } = useArrivalChecklistRaw(propertyId);
  return data ? arrivalSummary(data.raw) : null;
}

function Switch({ on, label, onChange }: { on: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="flex items-center gap-2.5 text-left"
    >
      <span
        className={cn(
          "flex h-[26px] w-[44px] shrink-0 rounded-full p-[3px]",
          on ? "justify-end bg-[#24a89a]" : "justify-start bg-[#cdd8df]",
        )}
      >
        <span className="h-5 w-5 rounded-full bg-white" />
      </span>
      <span className="text-[14px] font-bold text-[#475569]">{label}</span>
    </button>
  );
}

type Row = ArrivalTask & { editing?: boolean };

export function ArrivalChecklistSettings({ propertyId, onSaved }: { propertyId: string; onSaved?: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useArrivalChecklistRaw(propertyId);
  const [rows, setRows] = useState<Row[]>([]);
  const [isStarter, setIsStarter] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (!data || loaded.current) return;
    loaded.current = true;
    const eff = effectiveArrivalTasks(data.raw);
    setRows(eff.tasks.map((t) => ({ ...t })));
    setIsStarter(eff.isStarter);
  }, [data]);

  const update = (id: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addTask = () =>
    setRows((prev) => [...prev, { id: newArrivalTaskId(), text: "", required: false, needsPhoto: false, editing: true }]);

  const removeTask = (id: string) => {
    const index = rows.findIndex((r) => r.id === id);
    const removed = rows[index];
    if (!removed) return;
    setRows((prev) => prev.filter((r) => r.id !== id));
    toast({
      title: "Task deleted",
      action: (
        <ToastAction
          altText="Undo delete"
          onClick={() =>
            setRows((prev) => {
              const next = [...prev];
              next.splice(Math.min(index, next.length), 0, removed);
              return next;
            })
          }
        >
          Undo
        </ToastAction>
      ),
    });
  };

  const drop = (to: number) => {
    if (dragIndex == null || dragIndex === to) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(to > dragIndex ? to - 1 : to, 0, moved);
      return next;
    });
    setDragIndex(null);
    setOverIndex(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = serializeArrivalTasks(rows);
      const { error } = await supabase
        .from("properties")
        .update({ arrival_checklist: payload } as never)
        .eq("id", propertyId);
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["arrival-checklist", propertyId] });
      onSaved?.();
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-10 text-[#64748b]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const header = (
    <div className="text-[13px] font-medium leading-[1.45] text-[#8494a2]">
      Cleaners see these at the start of the Clean step, after the walk-through photos, before the cleaning checklist.
    </div>
  );

  return (
    <div className="flex flex-col gap-[18px]">
      {header}

      {rows.length === 0 ? (
        <div className="flex flex-col items-start gap-3 rounded-[14px] border border-dashed border-[#d3dee5] bg-[#f4f7f9] p-5">
          <div className="text-[15px] font-medium text-[#475569]">
            No tasks. The Clean step opens straight on the cleaning checklist.
          </div>
          <button
            type="button"
            onClick={addTask}
            className="flex h-11 items-center rounded-xl bg-[#24a89a] px-5 text-[15px] font-bold text-white"
          >
            Add a task
          </button>
        </div>
      ) : (
        <>
          <div className="text-[14px] font-medium leading-[1.5] text-[#475569]">
            {isStarter
              ? "A starter list to get you going. Edit, reorder or delete tasks to fit this property. Photos of the unit and problem reports already happen in the Walk step."
              : "Photos of the unit on arrival and problem reports already happen in the Walk step — no need to add them here."}
          </div>
          <div className="flex flex-col">
            {rows.map((row, i) => (
              <div key={row.id}>
                {overIndex === i && dragIndex != null && dragIndex !== i ? (
                  <div className="my-1 h-[3px] rounded-full bg-[#24a89a]" />
                ) : null}
                <div
                  onDragOver={(e) => {
                    if (dragIndex == null) return;
                    e.preventDefault();
                    setOverIndex(i);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    drop(i);
                  }}
                  className={cn(
                    "flex gap-3 rounded-[14px] border border-[#e6edf1] bg-white p-3.5",
                    i > 0 && "mt-2.5",
                    dragIndex === i && "opacity-50",
                  )}
                >
                  <div
                    draggable
                    aria-label="Drag to reorder"
                    onDragStart={() => setDragIndex(i)}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setOverIndex(null);
                    }}
                    className="grid h-8 w-5 shrink-0 cursor-grab grid-cols-2 content-center gap-1 self-start pt-1"
                  >
                    {[0, 1, 2, 3, 4, 5].map((d) => (
                      <span key={d} className="h-1 w-1 rounded-full bg-[#b0bcc4]" />
                    ))}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-3">
                    <div className="flex items-start gap-3">
                      {row.editing ? (
                        <>
                          <input
                            autoFocus
                            value={row.text}
                            onChange={(e) => update(row.id, { text: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && row.text.trim()) update(row.id, { editing: false });
                            }}
                            placeholder="What should the cleaner do?"
                            className="min-w-0 flex-1 rounded-[10px] border-[1.5px] border-[#24a89a] px-3 py-2 text-[15px] font-bold text-[#0f1720] outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (row.text.trim()) update(row.id, { editing: false });
                            }}
                            className="shrink-0 pt-2 text-[14px] font-bold text-[#15776d]"
                          >
                            Done
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => update(row.id, { editing: true })}
                            className="min-w-0 flex-1 text-left text-[15px] font-bold leading-snug text-[#0f1720]"
                          >
                            {row.text}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeTask(row.id)}
                            className="shrink-0 text-[13px] font-bold text-[#b4453a]"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      <Switch on={row.required} label="Required" onChange={(v) => update(row.id, { required: v })} />
                      <Switch on={row.needsPhoto} label="Needs a photo" onChange={(v) => update(row.id, { needsPhoto: v })} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div
              onDragOver={(e) => {
                if (dragIndex == null) return;
                e.preventDefault();
                setOverIndex(rows.length);
              }}
              onDrop={(e) => {
                e.preventDefault();
                drop(rows.length);
              }}
              className="h-3"
            >
              {overIndex === rows.length && dragIndex != null ? (
                <div className="my-1 h-[3px] rounded-full bg-[#24a89a]" />
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={addTask}
            className="self-start text-[15px] font-bold text-[#15776d] hover:text-[#1b8378]"
          >
            + Add a task
          </button>
        </>
      )}

      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        className="flex h-[50px] items-center justify-center rounded-[13px] bg-[#24a89a] text-[16px] font-bold text-white hover:bg-[#1b8378] disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
      </button>
    </div>
  );
}
